/**
 * Cryptography and Obfuscation Utilities
 * 
 * Provides transient protection for sensitive data (PII) stored in browser storage.
 * Mandate: NEVER TRUST THE FRONTEND. All stored PII must be obfuscated to prevent
 * opportunistic reading via developer tools or browser extensions.
 */

import logger from '../services/loggerService';

/**
 * Obfuscates a string using Base64 encoding.
 * NOTE: This is NOT strong encryption. It is intended for transient obfuscation 
 * of PII in sessionStorage (e.g., phone numbers, partial names).
 * 
 * @param text The plain text to obfuscate
 * @returns Obfuscated (Base64) string
 */
export function obfuscate(text: string): string {
    if (!text) return '';
    try {
        // Use btoa for Base64 encoding
        return btoa(encodeURIComponent(text));
    } catch (error) {
        logger.error('Obfuscation failed', error, 'cryptography.obfuscate');
        return text;
    }
}

/**
 * De-obfuscates a previously obfuscated Base64 string.
 * 
 * @param obfuscatedText The Base64 string to decode
 * @returns Original plain text or empty string if invalid
 */
export function deobfuscate(obfuscatedText: string): string {
    if (!obfuscatedText) return '';
    try {
        // Use atob for Base64 decoding
        return decodeURIComponent(atob(obfuscatedText));
    } catch (error) {
        logger.warn('De-obfuscation failed - data may be corrupted or not obfuscated', { error });
        return '';
    }
}

/**
 * Enhanced Object Obfuscation
 * Recursively obfuscates all string values in an object.
 */
export function obfuscateObject<T>(obj: T): T {
    if (typeof obj === 'string') {
        return obfuscate(obj) as unknown as T;
    }

    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => obfuscateObject(item)) as unknown as T;
    }

    const result: Record<string, unknown> = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = obfuscateObject((obj as Record<string, unknown>)[key]);
        }
    }
    return result as T;
}

/**
 * Enhanced Object De-obfuscation
 */
export function deobfuscateObject<T>(obj: T): T {
    if (typeof obj === 'string') {
        return deobfuscate(obj) as unknown as T;
    }

    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => deobfuscateObject(item)) as unknown as T;
    }

    const result: Record<string, unknown> = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = deobfuscateObject((obj as Record<string, unknown>)[key]);
        }
    }
    return result as T;
}
